import React from 'react';
import { motion } from 'framer-motion';
import { X, UserCog, Utensils, Dumbbell, CheckCircle2, Sparkles } from 'lucide-react';

/**
 * Onboarding Éclair — carte de bienvenue affichée sur l'accueil ("Mon suivi") pour tout nouveau
 * client (tous plans) juste après son paiement. 3 étapes auto-détectées (aucune saisie
 * supplémentaire) : profil, 1ère activité (repas/poids), 1er entraînement. Se masque dès que
 * les 3 sont faites ou que le client la ferme manuellement (jamais réaffichée ensuite).
 */
export const QuickStartCard = ({ en, steps, onNavigate, onDismiss }) => {
  const doneCount = steps.filter((s) => s.done).length;
  const allDone = doneCount === steps.length;

  if (allDone) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: -10 }} animate={{ opacity: 1, y: 0 }}
      className="w-full mb-6 rounded-2xl border border-primary/30 bg-gradient-to-br from-primary/10 via-black to-black p-5 relative overflow-hidden"
      data-testid="onboarding-quickstart-card"
    >
      <button
        onClick={onDismiss}
        className="absolute top-3 right-3 text-zinc-500 hover:text-white transition-colors"
        data-testid="onboarding-quickstart-dismiss"
        aria-label={en ? 'Close' : 'Fermer'}
      >
        <X className="w-4 h-4" />
      </button>

      <div className="flex items-center gap-2 mb-1">
        <Sparkles className="w-4 h-4 text-primary" />
        <p className="font-mono text-[10px] uppercase tracking-[0.2em] text-primary">
          {en ? 'Quick start · 1 minute' : 'Démarrage éclair · 1 minute'}
        </p>
      </div>
      <h3 className="text-white font-bold text-lg leading-tight mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
        {en ? 'Welcome! Let\'s get you started' : 'Bienvenue ! On vous lance en 1 minute'}
      </h3>

      <div className="space-y-2.5">
        {steps.map((step) => (
          <button
            key={step.key}
            onClick={() => !step.done && onNavigate(step.tab)}
            disabled={step.done}
            className={`w-full flex items-center gap-3 rounded-xl px-4 py-3 text-left transition-colors ${
              step.done
                ? 'bg-emerald-500/10 border border-emerald-500/20'
                : 'bg-white/5 border border-white/10 hover:border-primary/40 hover:bg-white/[0.08]'
            }`}
            data-testid={`onboarding-quickstart-step-${step.key}`}
          >
            {step.done ? (
              <CheckCircle2 className="w-5 h-5 text-emerald-400 shrink-0" />
            ) : (
              <step.Icon className="w-5 h-5 text-primary shrink-0" />
            )}
            <span className={`text-sm font-semibold flex-1 ${step.done ? 'text-emerald-300 line-through' : 'text-white'}`}>
              {step.label}
            </span>
            {!step.done && (
              <span className="text-xs font-bold text-primary shrink-0">{en ? 'Go →' : 'Go →'}</span>
            )}
          </button>
        ))}
      </div>

      <div className="mt-4 h-1.5 rounded-full bg-white/10 overflow-hidden" data-testid="onboarding-quickstart-progress">
        <div
          className="h-full bg-gradient-to-r from-primary to-pink-500 transition-all duration-500"
          style={{ width: `${(doneCount / steps.length) * 100}%` }}
        />
      </div>
    </motion.div>
  );
};

export const QUICKSTART_ICONS = { UserCog, Utensils, Dumbbell };

export default QuickStartCard;
